import { useState, useCallback, createContext, useContext } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastMessage {
  id: number;
  text: string;
  type: 'error' | 'warning' | 'success' | 'info';
  action?: ToastAction;
}

interface ToastContextType {
  toast: (text: string, type?: ToastMessage['type'], action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextType>({ toast: () => {} });

let nextId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const toast = useCallback((text: string, type: ToastMessage['type'] = 'error', action?: ToastAction) => {
    const id = ++nextId;
    setMessages((prev) => [...prev, { id, text, type, action }]);
    // Toasts with an action (e.g. Undo) stick around longer so there's
    // a comfortable window to click it.
    setTimeout(() => {
      setMessages((prev) => prev.filter((m) => m.id !== id));
    }, action ? 6500 : 4000);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toast container */}
      {messages.length > 0 && (
        <div style={toastContainer}>
          {messages.map((msg) => (
            <div key={msg.id} className="ui-toast" style={{ ...toastItem, ...typeStyles[msg.type] }}>
              <span style={{ display: 'flex', marginRight: 8, flexShrink: 0 }}>{typeIcons[msg.type]}</span>
              <span style={{ flex: 1 }}>{msg.text}</span>
              {msg.action && (
                <button
                  onClick={() => {
                    // Dismiss first so a slow undo request can't leave a
                    // clickable button that fires twice.
                    setMessages((prev) => prev.filter((m) => m.id !== msg.id));
                    msg.action!.onClick();
                  }}
                  style={actionBtn}
                >
                  {msg.action.label}
                </button>
              )}
              <button
                onClick={() => setMessages((prev) => prev.filter((m) => m.id !== msg.id))}
                style={dismissBtn}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

const typeIcons: Record<ToastMessage['type'], React.ReactNode> = {
  error: <AlertTriangle size={15} />,
  warning: <AlertTriangle size={15} />,
  success: <CheckCircle2 size={15} />,
  info: <Info size={15} />,
};

const toastContainer: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  maxWidth: 400,
};

const toastItem: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '10px 14px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  // Keyframes live in ui.css (the old 'slideIn' name was never defined, so
  // toasts used to pop in with no entrance at all).
  animation: 'ui-toast-in 0.2s ease-out',
};

const typeStyles: Record<ToastMessage['type'], React.CSSProperties> = {
  error:   { background: '#fff0f0', color: '#c33', border: '1px solid #fcc' },
  warning: { background: '#fff8e1', color: '#b8860b', border: '1px solid #ffe0a0' },
  success: { background: '#f0faf0', color: '#2a7d2a', border: '1px solid #b2e5b2' },
  info:    { background: '#f0f0ff', color: 'var(--brand)', border: '1px solid #d0ccff' },
};

const actionBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.6)',
  border: '1px solid currentColor',
  borderRadius: 6,
  cursor: 'pointer',
  color: 'inherit',
  fontSize: 12,
  fontWeight: 700,
  padding: '3px 10px',
  marginLeft: 10,
  flexShrink: 0,
};

const dismissBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  color: 'inherit',
  opacity: 0.6,
  padding: '0 2px',
  marginLeft: 4,
};
